<!-- BulkAddNoteForm.vue -->

<script setup>
import {
  removeTabsAndLineBreaks,
  toTitleCase,
  stripHtml,
} from "@/utils/stringUtils";
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  computed,
} from "vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import AddTagForm from "../Tag/AddTagForm.vue";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import AddTextLimitModal from "../Text/AddTextLimitModal.vue";
import UpgradePage from "@/components/User/UpgradePage.vue";
import TagCardModal from "@/components/Tag/TagCardModal.vue";

import { useTagStore } from "@/stores/useTagStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useUserStore } from "@/stores/useUserStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getDefaultAccessMode,
  getPublicModeHint,
} from "@/utils/permissionDefaults";
import { useGroupStore } from "@/stores/useGroupStore";

import NoteCustomTextarea from "./NoteCustomTextarea.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

// ====================================================================
// ✅ PROPS & EMITS
// ====================================================================
const props = defineProps({
  excerpts: {
    type: Array,
    default: () => [],
  },
  activityId: Number,
  createdByUserId: Number,
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "created"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. O componente fica montado e só
// alterna `show`, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

// ====================================================================
// ✅ ESTADOS DE PERSISTÊNCIA (MEMÓRIA ENTRE NOTAS / ABERTURAS)
// ====================================================================
const lastUsedSource = ref("");
const lastUsedTags = ref([]);
const lastUsedPermissionsMode = ref("public"); // "public" | "private" | "custom"
const lastUsedCustomPermissions = ref([]);
const lastUsedBody = ref("");

const isBodyExpanded = ref(false);
function handleToggleBodyExpand(expanded) {
  isBodyExpanded.value = !!expanded;
}

const showPlanCatalog = ref(false);
const showTextLimitModal = ref(false);
const textLimitInfo = ref({
  limit: null,
  currentUsage: null,
  newUsage: null,
  message: "",
});

const permissionFormKey = ref(0);
const permissionFormRef = ref(null);

const tagStore = useTagStore();
const noteStore = useNoteStore();
const userStore = useUserStore();
const permissionStore = usePermissionStore();
const groupStore = useGroupStore();

const selectedTag = ref([]);
const showAddTagModal = ref(false);
const allTags = ref([]);

const obj = ref({
  noteId: 0,
  activityId: props.activityId,
  tagIds: [],
  title: "",
  body: "",
  source: "",
  textRangeStart: null,
  textRangeEnd: null,
  permissions: [],
});

const showTagCardModal = ref(false);
const selectedTagIdForModal = ref(null);

function openTagCard(tagOption) {
  if (!tagOption?.value) return;
  selectedTagIdForModal.value = tagOption.value;
  showTagCardModal.value = true;
}

function closeTagCard() {
  showTagCardModal.value = false;
  selectedTagIdForModal.value = null;
}

// ====================================================================
// ✅ PATCH: atualizar chips de tag após edição no TagCardModal
// ====================================================================
// Ao editar uma tag via TagCardModal, o Multiselect não atualiza sozinho
// o label da chip já selecionada, porque selectedTag guarda um snapshot
// do objeto antigo. Por isso, buscamos novamente as tags, remontamos allTags
// e reprojetamos selectedTag para as novas referências.
async function handleTagUpdated() {
  // 1) Busca a lista fresca de tags no servidor.
  await tagStore.fetchTags();

  // 2) Recria as opções do Multiselect com os labels atualizados.
  atualizarSubtipos();

  // 3) Mantém a seleção atual, mas trocando os objetos antigos
  //    pelas novas referências existentes em allTags.
  const selectedIds = selectedTag.value.map((s) => s.value);

  selectedTag.value = selectedIds
    .map((id) => allTags.value.find((s) => s.value === id))
    .filter(Boolean);
}

const loggedUser = computed(() => userStore.currentUser);

// ====================================================================
// ✅ HELPERS: createdByUserName / SYS
// ====================================================================
function getCreatedByUserName(entity) {
  return (
    entity?.createdByUserName ??
    entity?.CreatedByUserName ??
    entity?.createdByUser?.userName ??
    entity?.CreatedByUser?.UserName ??
    ""
  );
}

function isSysEntity(entity) {
  return (
    (getCreatedByUserName(entity) || "").toString().trim().toLowerCase() ===
    "sys"
  );
}

function buildTagLabel(sub) {
  return (sub?.tagName || "").trim();
}

// ====================================================================
// ✅ TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// FUNÇÕES DE MODAL DE PLANO
// ====================================================================
function handleCloseLimitModal() {
  showTextLimitModal.value = false;
}
function handleOpenPlans() {
  showTextLimitModal.value = false;
  showPlanCatalog.value = true;
}
function handleClosePlanCatalog() {
  showPlanCatalog.value = false;
}

// ====================================================================
// ✅ HELPERS DE PERMISSÃO
// ====================================================================
function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find((g) => g.groupName?.toLowerCase() === "todos")
      ?.groupId ?? null
  );
}

function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}

const creatorUserId = computed(
  () => loggedUser.value?.userId ?? props.createdByUserId ?? null
);
const creatorUserName = computed(() => loggedUser.value?.userName ?? "");

function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");

  if (!creatorId || !visualizarId || !selecionarId || !editarId) {
    console.warn(
      "[BulkAddNoteForm] Não foi possível montar permissões públicas padrão",
      { todosId, creatorId, visualizarId, selecionarId, editarId }
    );
  }

  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

function buildPrivatePermissions() {
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];
  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

// Molde com que o formulário abre — regra em utils/permissionDefaults.js
// (getDefaultAccessMode): fechado para quem tem círculo (membro ou titular),
// público só para quem não tem.
function buildInitialPermissions() {
  return getDefaultAccessMode() === "private"
    ? buildPrivatePermissions()
    : buildPublicPermissions();
}

function permsMatch(a, b) {
  if (a.length !== b.length) return false;
  const key = (p) => `${p.type}|${p.entityId}|${p.permissionId}`;
  const setA = new Set(a.map(key));
  return b.every((p) => setA.has(key(p)));
}

// ====================================================================
// currentAccessMode — computed de 3 estados
// ====================================================================
const currentAccessMode = computed(() => {
  const perms = obj.value.permissions || [];
  if (perms.length === 0) return "public";
  const publicPerms = buildPublicPermissions();
  if (publicPerms.length > 0 && permsMatch(perms, publicPerms)) return "public";
  const privatePerms = buildPrivatePermissions();
  if (privatePerms.length > 0 && permsMatch(perms, privatePerms))
    return "private";
  return "custom";
});

// ====================================================================
// selectAccessMode — handler do segmented control
// ====================================================================
function selectAccessMode(mode) {
  if (isSubmitting.value) return;
  if (mode === "public") {
    obj.value.permissions = buildPublicPermissions();
  } else if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
  } else if (mode === "custom") {
    openPermissionModal();
  }
}

// ====================================================================
// Mini-modal de permissões
// ====================================================================
const showPermissionModal = ref(false);
const permissionBackup = ref([]);

// 🔹 NOVO: arrasto próprio do mini-modal, independente do painel principal.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

function openPermissionModal() {
  permissionBackup.value = (obj.value.permissions || []).map((p) => ({ ...p }));

  if (
    (!obj.value.permissions || obj.value.permissions.length === 0) &&
    lastUsedCustomPermissions.value.length > 0
  ) {
    obj.value.permissions = [...lastUsedCustomPermissions.value];
  }
  if (!obj.value.permissions || obj.value.permissions.length === 0) {
    obj.value.permissions = buildPublicPermissions();
  }

  showPermissionModal.value = true;
}

function confirmPermissionModal() {
  if (
    permissionFormRef.value &&
    typeof permissionFormRef.value.validateBeforeConfirm === "function"
  ) {
    const isValid = permissionFormRef.value.validateBeforeConfirm();
    if (!isValid) return;
  }
  showPermissionModal.value = false;
}

function cancelPermissionModal() {
  obj.value.permissions = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}

// ====================================================================
// ✅ FUNÇÕES DE TAGS
// ====================================================================
function atualizarSubtipos() {
  allTags.value = tagStore.tags.map((sub) => ({
    label: buildTagLabel(sub),
    value: sub.tagId,
    isSys: isSysEntity(sub),
  }));
}

const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({ id: u.userId, name: u.userName }))
);
const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.createdByUserId ?? g.CreatedByUserId ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);
const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

// ====================================================================
// ✅ NORMALIZAÇÃO DOS TRECHOS
// ====================================================================
const hasExcerpts = computed(
  () => Array.isArray(props.excerpts) && props.excerpts.length > 0
);

const normalizedExcerpts = computed(() => {
  const raw = Array.isArray(props.excerpts) ? props.excerpts : [];
  const seen = new Set();
  const unique = [];

  for (const ex of raw) {
    const text = String(ex.text || "").trim();
    const start =
      ex.offsetStart ?? ex.start ?? ex.textRangeStart ?? ex.begin ?? null;
    const end = ex.offsetEnd ?? ex.end ?? ex.textRangeEnd ?? ex.finish ?? null;
    const key = `${start ?? "null"}-${end ?? "null"}-${text}`;

    if (!seen.has(key)) {
      seen.add(key);
      unique.push({ text, start, end });
    }
  }

  return unique.map((ex, idx) => ({
    index: idx + 1,
    text: ex.text,
    start: ex.start,
    end: ex.end,
  }));
});

// ====================================================================
// ✅ CLOSE MODAL
// ====================================================================
const isSubmitting = ref(false);
const validationErrors = ref({});

function closeModal() {
  isSubmitting.value = false;

  lastUsedSource.value = obj.value.source;
  lastUsedTags.value = [...selectedTag.value];

  const currentMode = currentAccessMode.value;
  lastUsedPermissionsMode.value = currentMode;
  if (currentMode === "custom" && obj.value.permissions?.length) {
    lastUsedCustomPermissions.value = [...obj.value.permissions];
  }

  lastUsedBody.value = obj.value.body;

  // Não mexe no toast aqui — ele é independente (Teleport próprio) e deve
  // continuar visível/some sozinho mesmo depois do modal fechar.

  obj.value.body = "";
  obj.value.title = "";
  obj.value.source = "";
  obj.value.permissions = [];
  obj.value.tagIds = [];
  selectedTag.value = [];

  showPermissionModal.value = false;
  isBodyExpanded.value = false;
  permissionFormKey.value++;

  try {
    const sel = window.getSelection?.();
    if (sel && typeof sel.removeAllRanges === "function") {
      sel.removeAllRanges();
    }
  } catch (e) {
    console.warn("[BulkAddNoteForm] Falha ao limpar seleção:", e);
  }

  emit("close");
}

function recoverLastBody() {
  if (lastUsedBody.value) obj.value.body = lastUsedBody.value;
}

// ====================================================================
// WATCHERS
// ====================================================================
watch(
  () => props.show,
  async (visible) => {
    if (visible) {
      // 🔹 NOVO: o modal reabre sem desmontar — recentraliza o painel arrastado
      resetPosition();
      await tagStore.fetchTags();
      atualizarSubtipos();

      obj.value.source = lastUsedSource.value;
      const savedIds = lastUsedTags.value.map((t) => t.value);
      selectedTag.value = savedIds
        .map((id) => allTags.value.find((s) => s.value === id))
        .filter(Boolean);

      const mode = lastUsedPermissionsMode.value || getDefaultAccessMode();
      if (mode === "public") {
        obj.value.permissions = buildPublicPermissions();
      } else if (mode === "private") {
        obj.value.permissions = buildPrivatePermissions();
      } else if (
        mode === "custom" &&
        lastUsedCustomPermissions.value.length > 0
      ) {
        obj.value.permissions = [...lastUsedCustomPermissions.value];
      } else {
        obj.value.permissions = buildPublicPermissions();
      }

      await nextTick();
    }
  },
  { immediate: false }
);

watch(selectedTag, (newVal) => {
  obj.value.tagIds = newVal.map((s) => s.value);
});

const newTagName = ref("");
function addNewTag(newTag) {
  newTagName.value = toTitleCase(newTag);
  showAddTagModal.value = true;
}

const multiselectTagKey = ref(0);

async function handleTagCreated(created) {
  if (!obj.value.tagIds.includes(created.tagId)) {
    obj.value.tagIds.push(created.tagId);
  }
  await tagStore.fetchTags();
  atualizarSubtipos();
  const updatedSelection = obj.value.tagIds
    .map((id) => allTags.value.find((s) => s.value === id))
    .filter(Boolean);
  selectedTag.value = updatedSelection;
  showAddTagModal.value = false;
}

// ====================================================================
// CADASTRAR EM LOTE
// ====================================================================
async function cadastrarEmLote() {
  try {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    validationErrors.value = {};

    if (!hasExcerpts.value) {
      showErrorToast(
        "Nenhum trecho selecionado. Ative o modo múltiplo e selecione trechos para criar notas em lote."
      );
      isSubmitting.value = false;
      return;
    }

    if (currentAccessMode.value === "public") {
      if (!obj.value.permissions || obj.value.permissions.length === 0) {
        obj.value.permissions = buildPublicPermissions();
      }
    }

    const commonBody = stripHtml(obj.value.body || "");
    const list = normalizedExcerpts.value;

    if (!Array.isArray(list) || list.length === 0) {
      showErrorToast(
        "Não foi possível normalizar os trechos selecionados para criar as notas."
      );
      isSubmitting.value = false;
      return;
    }

    const batchPayload = [];

    for (const ex of list) {
      const titleForNote = removeTabsAndLineBreaks(ex.text || "", 100);

      if (!titleForNote) {
        showErrorToast(
          "Uma das notas não pôde ser criada: o trecho está vazio e não é possível definir o título."
        );
        isSubmitting.value = false;
        return;
      }

      const payload = JSON.parse(JSON.stringify(obj.value));
      // Título: usa o comum se preenchido; senão usa o trecho (já truncado a 100)
      payload.title = obj.value.title.trim()
        ? obj.value.title.trim()
        : titleForNote;
      payload.body = commonBody;
      payload.textRangeStart = ex.start ?? null;
      payload.textRangeEnd = ex.end ?? null;
      payload.public = currentAccessMode.value === "public";

      batchPayload.push(payload);
    }

    await noteStore.createNotesBatch(batchPayload, props.activityId);
    // createNotesBatch já atualiza a lista paginada; o "created" abaixo é
    // tratado por NoteSection.handleBulkNotesCreated, que resincroniza allNotes
    // (fonte dos destaques em TextWithNotes.vue) — sem repetir o fetch aqui.

    showSuccessToast("Notas criadas com sucesso.");
    emit("created");

    // Fecha o modal já — o toast é independente (Teleport próprio) e some
    // sozinho pelo seu timer, sem o modal sobreposto atrás.
    closeModal();
  } catch (error) {
    if (
      error.response?.status === 403 ||
      error.response?.data?.message?.includes("limite")
    ) {
      textLimitInfo.value = {
        limit: error.response?.data?.limit || null,
        currentUsage: error.response?.data?.currentUsage || null,
        newUsage: error.response?.data?.newUsage || null,
        message:
          error.response?.data?.message || "Limite de caracteres atingido",
      };
      showTextLimitModal.value = true;
      isSubmitting.value = false;
      return;
    }

    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao criar as notas em lote.";
    if (error.response?.data?.errors) {
      const ve = error.response.data.errors;
      const messages = [];
      for (const field in ve) {
        if (ve[field] && ve[field].length > 0) messages.push(ve[field][0]);
      }
      if (messages.length > 0) userFriendlyMessage = messages.join("\n");
    } else if (error.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    console.error(
      "Erro detalhado da API (BulkAddNoteForm - batch):",
      error.response?.data || error
    );

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

// ====================================================================
// onMounted / onBeforeUnmount
// ====================================================================
onMounted(async () => {
  await Promise.all([
    tagStore.fetchTags(),
    permissionStore.fetchAllPermissions(),
    groupStore.fetchGroups(1, 1000),
    userStore.fetchUsers(1, 1000),
  ]);

  atualizarSubtipos();
  obj.value.permissions = buildInitialPermissions();
  obj.value.body = "";
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div v-if="show" class="modal-backdrop" @click.self="!isSubmitting">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Criar Notas em Lote</h3>
        <button
          @click="closeModal"
          :disabled="isSubmitting"
          type="button"
          class="modal-close-btn"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form
        id="bulk-add-note-form"
        @submit.prevent="cadastrarEmLote"
        class="modal-form"
        novalidate
      >
        <div class="modal-body">
          <div class="note-two-cols">
            <!-- ESQUERDA -->
            <div
              class="note-col-left divider-line"
              :class="{ 'note-col-left-expanded': isBodyExpanded }"
            >
              <div class="step-content note-step-top">
                <div class="form-group" v-show="!isBodyExpanded">
                  <label for="subtype" class="form-label">Tag (opcional)</label>
                  <Multiselect
                    v-model="selectedTag"
                    :options="allTags"
                    :multiple="true"
                    placeholder="Selecione ou crie tags para as notas"
                    label="label"
                    track-by="value"
                    :taggable="true"
                    :allow-empty="true"
                    @tag="addNewTag"
                    :disabled="isSubmitting"
                    :key="multiselectTagKey"
                  >
                    <template #option="{ option }">
                      <div class="ms-option-row">
                        <span class="ms-option-text">{{ option.label }}</span>
                        <span v-if="option.isSys" class="sys-badge">SYS</span>
                      </div>
                    </template>
                    <template #tag="{ option, remove }">
                      <span class="ms-chip">
                        <button
                          type="button"
                          class="ms-chip-link"
                          @click.stop="openTagCard(option)"
                        >
                          {{ option.label }}
                        </button>
                        <span v-if="option.isSys" class="sys-badge">SYS</span>
                        <button
                          type="button"
                          class="ms-chip-remove"
                          @click.stop="remove(option)"
                          aria-label="Remover"
                        >
                          ×
                        </button>
                      </span>
                    </template>
                  </Multiselect>
                </div>

                <div class="form-group" v-show="!isBodyExpanded">
                  <label for="title" class="form-label"
                    >Título comum (opcional)</label
                  >
                  <input
                    v-model="obj.title"
                    type="text"
                    id="title"
                    class="input-base"
                    placeholder="Deixe em branco para usar o trecho como título"
                    :disabled="isSubmitting"
                  />
                </div>

                <div class="form-group" v-show="!isBodyExpanded">
                  <label for="source" class="form-label"
                    >Fonte (opcional)</label
                  >
                  <input
                    v-model="obj.source"
                    type="text"
                    id="source"
                    class="input-base"
                    placeholder="Digite a fonte das notas"
                    :disabled="isSubmitting"
                  />
                </div>

                <div
                  class="form-group"
                  :class="[isBodyExpanded ? 'body-expanded' : '']"
                >
                  <div class="flex justify-between items-center w-full">
                    <label for="body" class="form-label"
                      >Corpo comum (opcional)</label
                    >
                    <button
                      v-if="lastUsedBody"
                      type="button"
                      @click="recoverLastBody"
                      class="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors uppercase tracking-wider"
                      title="Usar o último conteúdo preenchido"
                    >
                      Preencher último
                    </button>
                  </div>
                  <NoteCustomTextarea
                    v-model:body="obj.body"
                    :disabled="isSubmitting"
                    @toggle-expand="handleToggleBodyExpand"
                  />

                  <p class="body-hint" v-show="!isBodyExpanded">
                    <i class="fas fa-lightbulb" aria-hidden="true"></i>
                    <span>
                      Cole o link de uma imagem, documento ou site para
                      exibi-lo no corpo. Use <code>[rótulo]</code> antes do
                      link para dar um nome a ele. Link entre aspas é
                      tratado como texto.
                    </span>
                  </p>
                </div>

                <!-- ✅ Segmented control de acesso (compacto — coluna esquerda) -->
                <div class="form-group" v-show="!isBodyExpanded">
                  <label class="form-label">Quem pode ver estas notas?</label>

                  <div class="access-segment access-segment--sm">
                    <button
                      type="button"
                      class="segment-btn"
                      :class="{
 'segment-btn--active': currentAccessMode === 'public',
                        'segment-btn--public': currentAccessMode === 'public',
                      }"
                      :disabled="isSubmitting"
                      @click="selectAccessMode('public')"
                    >
                      <i class="fas fa-globe-americas segment-btn__icon"></i>
                      <span class="segment-btn__label">Público</span>
                    </button>

                    <button
                      type="button"
                      class="segment-btn"
                      :class="{
 'segment-btn--active': currentAccessMode === 'private',
                        'segment-btn--private': currentAccessMode === 'private',
                      }"
                      :disabled="isSubmitting"
                      @click="selectAccessMode('private')"
                    >
                      <i class="fas fa-lock segment-btn__icon"></i>
                      <span class="segment-btn__label">Privado</span>
                    </button>

                    <button
                      type="button"
                      class="segment-btn"
                      :class="{
 'segment-btn--active': currentAccessMode === 'custom',
                        'segment-btn--custom': currentAccessMode === 'custom',
                      }"
                      :disabled="isSubmitting"
                      @click="selectAccessMode('custom')"
                    >
                      <i class="fas fa-user-shield segment-btn__icon"></i>
                      <span class="segment-btn__label">Personalizado</span>
                    </button>
                  </div>

                  <p class="access-hint">
                    <template v-if="currentAccessMode === 'public'">
                      <i class="fas fa-info-circle"></i>
                      {{ getPublicModeHint() }} poderão visualizar estas notas.
                    </template>
                    <template v-else-if="currentAccessMode === 'private'">
                      <i class="fas fa-info-circle"></i>
                      Somente você poderá acessar estas notas.
                    </template>
                    <template v-else>
                      <i class="fas fa-info-circle"></i>
                      Acesso restrito às pessoas configuradas.
                      <button
                        type="button"
                        class="access-hint__link"
                        @click="openPermissionModal"
                        :disabled="isSubmitting"
                      >
                        Editar permissões
                      </button>
                    </template>
                  </p>
                </div>

                <div style="flex-grow: 1" v-show="!isBodyExpanded"></div>
              </div>
            </div>

            <!-- DIREITA -->
            <div class="note-col-right">
              <div class="step-content h-full">
                <div class="form-group excerpts-panel">
                  <label class="form-label">
                    Trechos selecionados ({{ normalizedExcerpts.length }})
                  </label>

                  <div class="excerpt-scroll">
                    <p v-if="!hasExcerpts" class="excerpt-empty">
                      Nenhum trecho selecionado ainda. Ative o modo de seleção
                      múltipla e arraste o cursor sobre partes do texto para
                      empilhá-las aqui.
                    </p>

                    <ul v-else class="excerpt-list">
                      <li
                        v-for="excerpt in normalizedExcerpts"
                        :key="excerpt.index"
                        class="excerpt-item"
                      >
                        <div class="excerpt-index">{{ excerpt.index }}</div>
                        <blockquote
                          class="themed-blockquote whitespace-pre-wrap break-words font-mono excerpt-blockquote"
                          :title="excerpt.text"
                        >
                          {{ excerpt.text }}
                        </blockquote>
                      </li>
                    </ul>
                  </div>
                </div>

                <div style="flex-grow: 1"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="submit"
            form="bulk-add-note-form"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="mr-1 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <svg
              v-else
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span>{{
              isSubmitting ? "Criando notas..." : "Criar notas em lote"
            }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <TagCardModal
    :show="showTagCardModal"
    :tagId="selectedTagIdForModal"
    @close="closeTagCard"
    @tag-updated="handleTagUpdated"
  />

  <!-- Mini-modal de permissões -->
  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button
              type="button"
              class="permission-modal-close"
              @click="cancelPermissionModal"
              aria-label="Fechar Editar Permissões"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>
          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para
              estas notas.
            </p>
            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="obj.permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              entityType="Note"
              :creatorUserName="creatorUserName"
            />
          </div>
          <div class="permission-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelPermissionModal"
              :disabled="isSubmitting"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="confirmPermissionModal"
              :disabled="isSubmitting"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="showAddTagModal"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4"
      @click.self="showAddTagModal = false"
    >
      <div class="w-full max-w-2xl" @click.stop>
        <AddTagForm
          :initialName="newTagName"
          @tag-added="handleTagCreated"
          @close="showAddTagModal = false"
        />
      </div>
    </div>

    <AddTextLimitModal
      :show="showTextLimitModal"
      :info="textLimitInfo"
      entityType="notas"
      @close="handleCloseLimitModal"
      @open-plans="handleOpenPlans"
    />

    <!-- Mesmo fluxo de upgrade do DashboardLayout (catálogo + checkout);
         z-index acima do formulário de nota e do modal de limite -->
    <UpgradePage
      v-if="showPlanCatalog"
      :initial-step="2"
      :redirect-on-catalog-close="false"
      :z-index="10010"
      @close-upgrade-modal="handleClosePlanCatalog"
      @close="handleClosePlanCatalog"
    />

    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 96rem;
  min-height: 700px;
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-footer {
  bottom: 0;
}

/* =========================
   LAYOUT 2 COLUNAS
========================= */
.note-two-cols {
  display: flex;
  gap: 2rem;
  min-height: 0;
  flex: 1 1 auto;
}

.note-col-left,
.note-col-right {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
}

.note-col-left {
  overflow-y: auto;
  padding-right: 2rem;
}
.note-col-right {
  overflow: hidden;
}
.note-step-top {
  margin-top: -0.25rem;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.form-label {
  flex-shrink: 0;
}

/* Dica de rotulação/preview de links no campo Corpo */
.body-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin: 0.4rem 0 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--text-muted);
}
.body-hint i {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: var(--warning);
  flex-shrink: 0;
}
.body-hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.7rem;
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
  background-color: var(--surface-3);
  color: #4338ca;
}

.divider-line {
  position: relative;
}
.divider-line::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  background-color: var(--border-color);
  height: 100%;
}

.themed-blockquote {
  background-color: var(--bg-header);
  border-left: 4px solid var(--brand-light);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: var(--text-dark);
  box-sizing: border-box;
  font-size: 0.85rem;
}

.excerpt-blockquote {
  height: auto;
  max-height: 5.5rem;
  overflow-y: auto;
}

/* =========================
   SEGMENTED CONTROL DE ACESSO
========================= */
.access-segment {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.segment-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background-color: var(--surface-2);
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--text-muted);
}

.access-segment--sm .segment-btn {
  padding: 0.45rem 0.35rem;
  border-radius: 10px;
}

.segment-btn:hover:not(:disabled):not(.segment-btn--active) {
  border-color: var(--neutral-400);
  background-color: var(--surface-3);
  color: var(--text-dark);
  transform: translateY(-1px);
}

.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-btn__icon {
  font-size: 1rem;
  line-height: 1;
}
.access-segment--sm .segment-btn__icon {
  font-size: 0.85rem;
}

.segment-btn__label {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
}
.access-segment--sm .segment-btn__label {
  font-size: 0.72rem;
}

.segment-btn--public.segment-btn--active {
  border-color: var(--success);
  background-color: var(--success-bg);
  color: var(--success-text);
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.12);
}

.segment-btn--private.segment-btn--active {
  border-color: var(--warning);
  background-color: var(--warning-bg);
  color: var(--warning-text);
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.12);
}

.segment-btn--custom.segment-btn--active {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
  box-shadow: 0 2px 8px rgba(155, 132, 255, 0.15);
}

.access-hint {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  line-height: 1.4;
}

.access-hint i {
  font-size: 0.72rem;
  flex-shrink: 0;
}

.access-hint__link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.access-hint__link:hover:not(:disabled) {
  color: #7c63f5;
}
.access-hint__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* =========================
   CHIPS
========================= */
.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 7px;
  border: 0px solid var(--neutral-400);
  background: var(--surface-3);
  font-size: 0.85rem;
  margin: 2px 6px 2px 0;
}
.ms-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
  color: var(--text-muted);
}
.ms-chip-remove:hover {
  color: #0f172a;
}
.ms-chip-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  line-height: 1.1;
  text-decoration: none;
}
.ms-chip-link:hover {
  color: var(--link);
  text-decoration: underline;
}
.ms-chip-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  border-radius: 6px;
}
.ms-option-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ms-option-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.sys-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 6px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(226, 232, 240, 0.8);
  color: rgba(51, 65, 85, 0.95);
  flex-shrink: 0;
  line-height: 1;
  white-space: nowrap;
}

/* =========================
   EXPANSÃO DO CORPO
========================= */
.body-expanded {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.body-expanded :deep(.ct-wrapper) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.body-expanded :deep(.ct-textarea) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.note-col-left-expanded {
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
}
.note-col-left-expanded .step-content {
  flex: 1 1 auto;
  min-height: 0;
}

/* =========================
   MINI-MODAL DE PERMISSÕES
========================= */
.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}
.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background-color: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}
/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}
.permission-modal-close {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.permission-modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}
.permission-modal-close svg {
  width: 1.25rem;
  height: 1.25rem;
}
.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
  min-height: 0;
  overflow: hidden;
}
.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
  line-height: 1.35;
  display: block;
}
.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}
:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}
:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border: 0px solid var(--neutral-200);
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}
:deep(.table-wrap::-webkit-scrollbar) {
  width: 6px;
}
:deep(.table-wrap::-webkit-scrollbar-thumb) {
  background-color: var(--neutral-400);
  border-radius: 10px;
}
.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* =========================
   LISTA DE TRECHOS (DIREITA)
========================= */
.excerpt-empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.3;
}

.excerpt-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.excerpt-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 6px;
  align-items: flex-start;
  padding: 4px 0;
  background: transparent !important;
  border: none !important;
}
.excerpt-item:hover {
  background: transparent !important;
  border: none !important;
}

.excerpt-index {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background-color: var(--brand-soft);
  color: var(--text-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.excerpts-panel {
  flex: 1 1 auto;
  min-height: 0;
}

.excerpt-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
}
.excerpt-scroll::-webkit-scrollbar {
  width: 6px;
}
.excerpt-scroll::-webkit-scrollbar-thumb {
  background-color: var(--neutral-400);
  border-radius: 10px;
}

</style>
